import { Badge } from '@/components/ui/badge';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { z } from 'zod';
import {
  FormSection,
  MasterDataForm,
} from '../components/master-data/MasterDataForm';
import {
  FilterConfig,
  MasterDataTable,
  TableColumn,
} from '../components/master-data/MasterDataTable';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { District, State } from '../types/district';

const statusOptions = [
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
];

const DistrictManagement = () => {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingDistrict, setEditingDistrict] = useState<District | null>(null);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const { axios } = useAuth();
  const { toast } = useToast();

  // State for server-side pagination, filtering, and searching
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentFilters, setCurrentFilters] = useState<Record<string, string>>(
    {}
  );
  const [sortConfig, setSortConfig] = useState<{
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }>({});

  // Fetch districts with server-side filtering and pagination (like State management)
  const {
    data: districtsResponse,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [
      'districts',
      currentPage,
      pageSize,
      searchTerm,
      currentFilters,
      sortConfig,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('limit', pageSize.toString());

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      // Add filters
      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });

      // Add sorting
      if (sortConfig.sortBy) {
        params.append('sortBy', sortConfig.sortBy);
        params.append('sortOrder', sortConfig.sortOrder || 'asc');
      }

      const response = await axios.get(
        `/api/master-data/district?${params.toString()}`
      );
      const result = response.data.data || {};
      console.log('District API response:', result);

      return {
        data: result.data || [],
        pagination: {
          total: result.total || 0,
          page: result.page || 1,
          limit: result.limit || 10,
          totalPages: result.totalPages || 1,
        },
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  const districts = districtsResponse?.data || [];
  const pagination = districtsResponse?.pagination;

  // Fetch states for dropdown (exactly like State management)
  const { data: statesData = [] } = useQuery({
    queryKey: ['states-dropdown'],
    queryFn: async () => {
      const response = await axios.get('/api/master-data/state');
      console.log('DistrictManagement - States API Response:', response.data);
      const statesData =
        response.data.data?.data?.map((state: any) => ({
          value: state._id, // Use state ID as value
          label: `${state.name} (${state.code})`,
          country: state.country,
        })) || [];
      console.log('DistrictManagement - States for dropdown:', statesData);
      return statesData;
    },
  });

  // Fetch countries for filtering states (exactly like State management)
  const { data: countriesData = [] } = useQuery({
    queryKey: ['countries-dropdown'],
    queryFn: async () => {
      const response = await axios.get('/api/master-data/country');
      console.log(
        'DistrictManagement - Countries API Response:',
        response.data
      );
      const countriesData =
        response.data.data?.data?.map((country: any) => ({
          value: country._id, // Use country ID as value
          label: `${country.name} (${country.code})`,
        })) || [];
      console.log(
        'DistrictManagement - Countries for dropdown:',
        countriesData
      );
      return countriesData;
    },
  });

  const stateOptions = statesData;
  const countryOptions = countriesData;

  // Show debugging info
  React.useEffect(() => {
    console.log('Districts API Response:', districtsResponse);
    console.log('Districts data:', districts);
    console.log('Districts pagination:', pagination);
    console.log('Districts length:', districts.length);
    console.log('Is districts array?', Array.isArray(districts));
    console.log('States data:', statesData);
    console.log('State options:', stateOptions);
    console.log('Countries data:', countriesData);
    console.log('Country options:', countryOptions);

    // Check individual district object
    if (districts.length > 0) {
      console.log('First district object:', districts[0]);
      console.log('District keys:', Object.keys(districts[0]));
    }
  }, [
    districtsResponse,
    districts,
    pagination,
    statesData,
    stateOptions,
    countriesData,
    countryOptions,
  ]);

  // Create district mutation
  const createDistrictMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await axios.post('/api/master-data/district', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['districts'] });
      queryClient.invalidateQueries({
        queryKey: ['master-data-count', 'district'],
      });
      toast({
        title: 'District created',
        description: 'District has been created successfully.',
      });
      setIsFormOpen(false);
      setEditingDistrict(null);
      setServerErrors({});
    },
    onError: (error: any) => {
      // Extract validation errors if available
      const validationErrors = error.response?.data?.errors || {};
      if (Object.keys(validationErrors).length > 0) {
        setServerErrors(validationErrors);
      } else {
        setServerErrors({});
        toast({
          title: 'Error creating district',
          description:
            error.response?.data?.error?.message ||
            error.response?.data?.error ||
            'Failed to create district',
          variant: 'destructive',
        });
      }
    },
  });

  // Update district mutation
  const updateDistrictMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await axios.put(`/api/master-data/district/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['districts'] });
      queryClient.invalidateQueries({
        queryKey: ['master-data-count', 'district'],
      });
      toast({
        title: 'District updated',
        description: 'District has been updated successfully.',
      });
      setIsFormOpen(false);
      setEditingDistrict(null);
      setServerErrors({});
    },
    onError: (error: any) => {
      // Extract validation errors if available
      const validationErrors = error.response?.data?.errors || {};
      if (Object.keys(validationErrors).length > 0) {
        setServerErrors(validationErrors);
      } else {
        setServerErrors({});
        toast({
          title: 'Error updating district',
          description:
            error.response?.data?.error?.message ||
            error.response?.data?.error ||
            'Failed to update district',
          variant: 'destructive',
        });
      }
    },
  });

  // Delete district mutation
  const deleteDistrictMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await axios.delete(
        `/api/master-data/district/${id}?soft=false`
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['districts'] });
      queryClient.invalidateQueries({
        queryKey: ['master-data-count', 'district'],
      });
      toast({
        title: 'District deleted',
        description: 'District has been deleted successfully.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error deleting district',
        description: error.response?.data?.error || 'Failed to delete district',
        variant: 'destructive',
      });
    },
  });

  // Table columns configuration with better responsive design
  const columns: TableColumn<District>[] = [
    {
      key: 'name',
      label: 'District Name',
      sortable: true,
      render: (value, district) => (
        <div className='space-y-1'>
          <div className='font-medium text-sm'>{district.name}</div>
          <div className='text-xs text-muted-foreground font-mono'>
            {district.code}
          </div>
        </div>
      ),
    },
    {
      key: 'code',
      label: 'Code',
      sortable: true,
      hideOnTablet: true,
      render: (value, district) => (
        <div className='font-mono text-sm bg-muted px-2 py-1 rounded'>
          {district.code}
        </div>
      ),
    },
    {
      key: 'state',
      label: 'State',
      sortable: true,
      render: (value, district) => (
        <div className='space-y-1'>
          <div className='font-medium text-sm'>
            {typeof district.state === 'object'
              ? (district.state as State).name
              : 'Unknown State'}
          </div>
          {typeof district.state === 'object' &&
            (district.state as State).code && (
              <div className='text-xs text-muted-foreground font-mono'>
                {(district.state as State).code}
              </div>
            )}
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value, district) => (
        <Badge
          variant={district.status === 'Active' ? 'default' : 'secondary'}
          className={
            district.status === 'Active'
              ? 'bg-green-100 text-green-800 hover:bg-green-100'
              : 'bg-red-100 text-red-800 hover:bg-red-100'
          }
        >
          {district.status}
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      hideOnTablet: true,
      render: (value, district) => (
        <div className='text-sm text-muted-foreground'>
          {new Date(district.createdAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })}
        </div>
      ),
    },
    {
      key: 'updatedAt',
      label: 'Updated',
      sortable: true,
      hideOnTablet: true,
      render: (value, district) => (
        <div className='text-sm text-muted-foreground'>
          {new Date(district.updatedAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })}
        </div>
      ),
    },
  ];

  // Enhanced filter configuration
  const enhancedFilterConfig: FilterConfig[] = [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [{ value: 'all', label: 'All Statuses' }, ...statusOptions],
      placeholder: 'Filter by status',
    },
    {
      key: 'country',
      label: 'Country',
      type: 'select',
      options: [
        { value: 'all', label: 'All Countries' },
        ...(countriesData || []).map((country: any) => ({
          value: country.value,
          label: country.label,
        })),
      ],
      placeholder: 'Filter by country',
    },
    {
      key: 'state',
      label: 'State',
      type: 'select',
      options: [
        { value: 'all', label: 'All States' },
        ...(statesData || [])
          .filter((state: any) => {
            // Filter states by selected country if any
            if (currentFilters.country && currentFilters.country !== 'all') {
              return state.country === currentFilters.country;
            }
            return true;
          })
          .map((state: any) => ({
            value: state.value,
            label: state.label,
          })),
      ],
      placeholder: 'Filter by state',
    },
  ];

  // Form sections configuration with better layout
  const formSections: FormSection[] = [
    {
      title: 'District Information',
      description: 'Configure district details and settings.',
      fields: [
        {
          name: 'name',
          label: 'District Name',
          type: 'text',
          required: true,
          placeholder: 'Enter district name (e.g., "Mumbai", "Delhi North")',
          description: 'A descriptive name for this district',
          validation: z
            .string()
            .min(2, 'Name must be at least 2 characters')
            .max(100, 'Name cannot exceed 100 characters')
            .regex(
              /^[a-zA-Z0-9\s\-_().]+$/,
              'Name can only contain letters, numbers, spaces, hyphens, underscores, and parentheses'
            ),
        },
        {
          name: 'code',
          label: 'District Code',
          type: 'text',
          required: true,
          placeholder: 'Enter district code (e.g., "MUM", "DLHN")',
          description: 'A unique short code for this district',
          validation: z
            .string()
            .min(1, 'Code is required')
            .regex(
              /^[A-Z0-9_-]+$/i,
              'Code can only contain letters, numbers, underscores and hyphens'
            ),
        },
        {
          name: 'state',
          label: 'State',
          type: 'select',
          required: true,
          options: (statesData || []).map((state: any) => ({
            value: state.value,
            label: state.label,
          })),
          placeholder: 'Select state',
          description: 'The state this district belongs to',
        },
        {
          name: 'status',
          label: 'Status',
          type: 'radio',
          required: true,
          options: [
            { value: 'Active', label: 'Active - Available for use' },
            { value: 'Inactive', label: 'Inactive - Not available for use' },
          ],
          description: 'Set the availability status for this district',
        },
      ],
    },
  ];

  const handleCreate = () => {
    setEditingDistrict(null);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const handleEdit = (district: District) => {
    // Transform data for form editing
    const transformedDistrict = {
      ...district,
      // Ensure state is the ID if it's populated
      state:
        typeof district.state === 'object'
          ? (district.state as State)._id
          : district.state,
      // Ensure status is properly set for radio buttons
      status: district.status,
    };
    setEditingDistrict(transformedDistrict);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const handleDelete = (district: District) => {
    if (
      window.confirm(
        `Are you sure you want to permanently delete "${district.name}"? This action cannot be undone.`
      )
    ) {
      deleteDistrictMutation.mutate(district._id);
    }
  };

  const handleSubmit = (data: any) => {
    // Transform form data for better API compatibility
    const transformedData = {
      ...data,
      // Convert string 'Active'/'Inactive' to proper status if needed
      status: data.status,
      // Ensure code is uppercase
      code: data.code?.toUpperCase(),
    };

    if (editingDistrict) {
      updateDistrictMutation.mutate({
        id: editingDistrict._id,
        data: transformedData,
      });
    } else {
      createDistrictMutation.mutate(transformedData);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingDistrict(null);
    setServerErrors({});
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['districts'] });
    toast({
      title: 'Data refreshed',
      description: 'District data has been refreshed successfully.',
    });
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();

      // Add current search and filters to export
      if (searchTerm) {
        params.append('search', searchTerm);
      }

      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });

      const response = await axios.get(
        `/api/master-data/district/export?${params.toString()}`,
        {
          responseType: 'blob',
        }
      );

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `districts_${timestamp}.xlsx`);

      // Append to html link element page
      document.body.appendChild(link);

      // Start download
      link.click();

      // Clean up and remove the link
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: 'Export successful',
        description: 'District data has been exported successfully.',
      });
    } catch (error: any) {
      toast({
        title: 'Export failed',
        description:
          error.response?.data?.error || 'Failed to export district data',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-3xl font-bold tracking-tight'>
            District Management
          </h1>
          <p className='text-muted-foreground'>
            Manage district master data with comprehensive controls
          </p>
        </div>
      </div>

      <MasterDataTable<District>
        title='Districts'
        description='Manage and configure district information'
        data={districts}
        columns={columns}
        isLoading={isLoading}
        // Pagination
        serverSide={true}
        pagination={pagination}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        // Search
        currentSearch={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder='Search districts by name or code...'
        // Filtering
        currentFilters={currentFilters}
        onFiltersChange={setCurrentFilters}
        filters={enhancedFilterConfig}
        // Sorting
        currentSort={sortConfig}
        onSortChange={setSortConfig}
        // Actions
        onRefresh={handleRefresh}
        onExport={handleExport}
        onAdd={handleCreate}
        onEdit={handleEdit}
        onDelete={handleDelete}
        // Permissions
        allowImportExport={true}
      />

      <MasterDataForm
        isOpen={isFormOpen}
        onClose={handleFormClose}
        title={editingDistrict ? 'Edit District' : 'Create District'}
        sections={formSections}
        initialData={editingDistrict}
        onSubmit={handleSubmit}
        isLoading={
          createDistrictMutation.isPending || updateDistrictMutation.isPending
        }
        mode={editingDistrict ? 'edit' : 'create'}
        serverErrors={serverErrors}
      />
    </div>
  );
};

export default DistrictManagement;
